import { useMemo } from 'react';
import { AspectRatio } from '../../core/template.schemas';

/**
 * Standard canvas dimensions for different aspect ratios
 * Diese werden sowohl für Display als auch Export verwendet
 */
export const EXPORT_DIMENSIONS = {
  '1:1': { width: 1080, height: 1080 },
  '16:9': { width: 1920, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
  '4:5': { width: 1080, height: 1350 },
  '4:3': { width: 1440, height: 1080 },
  '3:4': { width: 1080, height: 1440 },
} as const;

/**
 * Gibt die Canvas-Dimensionen für ein Aspect Ratio zurück
 * Diese Dimensionen werden für alles verwendet: Display, Export, Element-Positionierung
 */
export const getCanvasDimensions = (aspectRatio: AspectRatio) => {
  return EXPORT_DIMENSIONS[aspectRatio] || EXPORT_DIMENSIONS['1:1'];
};

/**
 * Maximum display dimensions for responsive UI
 */
export const MAX_DISPLAY_DIMENSIONS = {
  desktop: { width: 800, height: 600 },
  mobile: { width: 350, height: 400 },
} as const;

export interface TemplateDimensions {
  /** Actual canvas dimensions for export and data storage */
  canvas: { width: number; height: number };
  /** Display dimensions for UI rendering (scaled down) */
  display: { width: number; height: number };
  /** Scale factor from display to canvas */
  scale: number;
}

/**
 * Hook for managing template dimensions consistently
 */
export const useTemplateDimensions = (
  aspectRatio: AspectRatio,
  isMobile: boolean = false
): TemplateDimensions => {
  return useMemo(() => {
    // Get export dimensions based on aspect ratio
    const canvas = EXPORT_DIMENSIONS[aspectRatio] || EXPORT_DIMENSIONS['1:1'];
    
    // Calculate display dimensions
    const maxDisplay = isMobile 
      ? MAX_DISPLAY_DIMENSIONS.mobile 
      : MAX_DISPLAY_DIMENSIONS.desktop;
    
    // Scale down to fit display constraints while maintaining aspect ratio
    const scaleX = maxDisplay.width / canvas.width;
    const scaleY = maxDisplay.height / canvas.height;
    const displayScale = Math.min(scaleX, scaleY, 1); // Never scale up
    
    const display = {
      width: Math.round(canvas.width * displayScale),
      height: Math.round(canvas.height * displayScale),
    };
    
    return {
      canvas,
      display,
      scale: displayScale,
    };
  }, [aspectRatio, isMobile]);
};